// @ts-nocheck
/**
 * Content Library Component for Teachers
 * Manage educational content, resources, and materials
 */

import React, { useState, useEffect } from 'react';
import {
  DataTable,
  Button,
  Tile,
  Grid,
  Column,
  Stack,
  Tag,
  InlineNotification,
  Modal,
  Form,
  FormGroup,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  FileUploader,
  Loading,
  Search,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@carbon/react';

const {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} = DataTable;

import {
  Document,
  DocumentPdf,
  Video,
  Image,
  Upload,
  Download,
  Share,
  TrashCan,
  Edit,
  FolderAdd,
  View,
} from '@carbon/icons-react';

import { api } from '../../services/authService';
import { useAuthStore } from '../../store/authStore';

interface ContentItem {
  content_id: string;
  title: string;
  description: string;
  content_type: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  subject: string;
  grade_level?: string;
  status: string;
  created_at: string;
  updated_at: string;
  assignment_count?: number;
  download_count?: number;
  shared_with?: number;
}

interface UploadFormData {
  title: string;
  description: string;
  subject: string;
  contentType: string;
  gradeLevel: string;
  file?: File;
}

const ContentLibrary: React.FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState<UploadFormData>({
    title: '',
    description: '',
    subject: '',
    contentType: 'course_material',
    gradeLevel: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchContentLibrary();
  }, []);

  const fetchContentLibrary = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/teacher/content/library');

      if (response.data?.success && response.data?.data?.content) {
        setContentItems(response.data.data.content);
      } else {
        // Use fallback data if API doesn't return expected structure
        setContentItems(generateMockContent());
      }
    } catch (error) {
      console.error('Failed to fetch content library:', error);
      // Use mock data as fallback
      setContentItems(generateMockContent());
    } finally {
      setLoading(false);
    }
  };

  const generateMockContent = (): ContentItem[] => {
    return [
      {
        content_id: '1',
        title: 'Algebra Basics - Chapter 1',
        description: 'Introduction to algebraic concepts and equations',
        content_type: 'course_material',
        file_path: '/content/algebra-ch1.pdf',
        file_size: 2048000,
        mime_type: 'application/pdf',
        subject: 'Mathematics',
        grade_level: 'Grade 9',
        status: 'active',
        created_at: new Date(Date.now() - 7 * 86400000).toISOString(),
        updated_at: new Date(Date.now() - 2 * 86400000).toISOString(),
        assignment_count: 3,
        download_count: 45,
        shared_with: 2,
      },
      {
        content_id: '2',
        title: 'Photosynthesis Experiment Guide',
        description: 'Lab guide for photosynthesis experiments',
        content_type: 'lab_guide',
        file_path: '/content/photosynthesis-lab.pdf',
        file_size: 1536000,
        mime_type: 'application/pdf',
        subject: 'Science',
        grade_level: 'Grade 10',
        status: 'active',
        created_at: new Date(Date.now() - 14 * 86400000).toISOString(),
        updated_at: new Date(Date.now() - 5 * 86400000).toISOString(),
        assignment_count: 1,
        download_count: 28,
        shared_with: 0,
      },
      {
        content_id: '3',
        title: 'Shakespeare Study Guide',
        description: 'Comprehensive guide to understanding Shakespeare',
        content_type: 'study_guide',
        file_path: '/content/shakespeare-guide.pdf',
        file_size: 3072000,
        mime_type: 'application/pdf',
        subject: 'English',
        grade_level: 'Grade 11',
        status: 'active',
        created_at: new Date(Date.now() - 21 * 86400000).toISOString(),
        updated_at: new Date(Date.now() - 10 * 86400000).toISOString(),
        assignment_count: 2,
        download_count: 67,
        shared_with: 5,
      },
    ];
  };

  const handleFileUpload = async () => {
    try {
      if (!uploadForm.file) {
        return;
      }

      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('subject', uploadForm.subject);
      formData.append('contentType', uploadForm.contentType);
      formData.append('gradeLevel', uploadForm.gradeLevel || '');

      const response = await api.post('/v1/teacher/content/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data?.success) {
        setIsUploadModalOpen(false);
        setUploadForm({
          title: '',
          description: '',
          subject: '',
          contentType: 'course_material',
          gradeLevel: '',
        });
        fetchContentLibrary();
      }
    } catch (error) {
      console.error('Failed to upload content:', error);
    }
  };

  const handleShare = async (contentId: string) => {
    setIsShareModalOpen(true);
    // Implementation for sharing would go here
  };

  const handleDelete = async (contentId: string) => {
    // Implementation for delete would go here
    console.log('Delete content:', contentId);
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return <DocumentPdf size={20} />;
    if (mimeType.includes('video')) return <Video size={20} />;
    if (mimeType.includes('image')) return <Image size={20} />;
    return <Document size={20} />;
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const filteredContent = contentItems.filter(item => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSubject = filterSubject === 'all' || item.subject === filterSubject;
    const matchesType = filterType === 'all' || item.content_type === filterType;
    return matchesSearch && matchesSubject && matchesType;
  });

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Loading withOverlay={false} />
        <div style={{ marginTop: 16 }}>Loading content library...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 400, marginBottom: 8 }}>Content Library</h1>
        <p style={{ color: '#525252' }}>Manage and organize your educational content and resources</p>
      </div>

      {/* Statistics */}
      <Grid style={{ marginBottom: 24 }}>
        <Column sm={4} md={2} lg={4}>
          <Tile>
            <div style={{ marginBottom: 8 }}>
              <Document size={24} style={{ color: '#0f62fe' }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: '#525252' }}>Total Content</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>{contentItems.length}</div>
          </Tile>
        </Column>
        <Column sm={4} md={2} lg={4}>
          <Tile>
            <div style={{ marginBottom: 8 }}>
              <Download size={24} style={{ color: '#198038' }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: '#525252' }}>Total Downloads</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>
              {contentItems.reduce((sum, item) => sum + (item.download_count || 0), 0)}
            </div>
          </Tile>
        </Column>
        <Column sm={4} md={2} lg={4}>
          <Tile>
            <div style={{ marginBottom: 8 }}>
              <Share size={24} style={{ color: '#8a3ffc' }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: '#525252' }}>Shared Items</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>
              {contentItems.filter(item => (item.shared_with || 0) > 0).length}
            </div>
          </Tile>
        </Column>
      </Grid>

      {/* Tabs for different content views */}
      <Tabs>
        <TabList>
          <Tab>All Content</Tab>
          <Tab>My Uploads</Tab>
          <Tab>Shared with Me</Tab>
          <Tab>Recently Used</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {/* Content Table */}
            <DataTable
              rows={filteredContent.map(item => ({ ...item, id: item.content_id }))}
              headers={[
                { key: 'title', header: 'Title' },
                { key: 'subject', header: 'Subject' },
                { key: 'content_type', header: 'Type' },
                { key: 'file_size', header: 'Size' },
                { key: 'assignment_count', header: 'Used In' },
                { key: 'actions', header: 'Actions' },
              ]}
              render={({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                <TableContainer>
                  <TableToolbar>
                    <TableToolbarContent>
                      <TableToolbarSearch
                        placeholder="Search content..."
                        onChange={(e: any) => setSearchTerm(e.target.value)}
                      />
                      <Select
                        id="subject-filter"
                        labelText=""
                        value={filterSubject}
                        onChange={e => setFilterSubject(e.target.value)}
                      >
                        <SelectItem value="all" text="All Subjects" />
                        <SelectItem value="Mathematics" text="Mathematics" />
                        <SelectItem value="Science" text="Science" />
                        <SelectItem value="English" text="English" />
                      </Select>
                      <Button kind="primary" renderIcon={Upload} onClick={() => setIsUploadModalOpen(true)}>
                        Upload Content
                      </Button>
                    </TableToolbarContent>
                  </TableToolbar>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, i) => {
                        const item = filteredContent[i];
                        return (
                          <TableRow {...getRowProps({ row })} key={row.id}>
                            <TableCell>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {getFileIcon(item.mime_type)}
                                <div>
                                  <div style={{ fontWeight: 500 }}>{item.title}</div>
                                  <div style={{ fontSize: '0.875rem', color: '#525252' }}>{item.description}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Tag type="blue">{item.subject}</Tag>
                            </TableCell>
                            <TableCell>{item.content_type.replace('_', ' ')}</TableCell>
                            <TableCell>{formatFileSize(item.file_size)}</TableCell>
                            <TableCell>{item.assignment_count} assignments</TableCell>
                            <TableCell>
                              <Stack gap={3} orientation="horizontal">
                                <Button kind="ghost" size="sm" renderIcon={View}>
                                  View
                                </Button>
                                <Button
                                  kind="ghost"
                                  size="sm"
                                  renderIcon={Share}
                                  onClick={() => handleShare(item.content_id)}
                                >
                                  Share
                                </Button>
                                <Button
                                  kind="ghost"
                                  size="sm"
                                  renderIcon={TrashCan}
                                  onClick={() => handleDelete(item.content_id)}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            />
          </TabPanel>
          <TabPanel>
            <InlineNotification
              kind="info"
              title="Coming Soon"
              subtitle="The 'My Uploads' view will show only content you've uploaded."
            />
          </TabPanel>
          <TabPanel>
            <InlineNotification
              kind="info"
              title="Coming Soon"
              subtitle="The 'Shared with Me' view will show content shared by other teachers."
            />
          </TabPanel>
          <TabPanel>
            <InlineNotification
              kind="info"
              title="Coming Soon"
              subtitle="The 'Recently Used' view will show your recently accessed content."
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Upload Modal */}
      <Modal
        open={isUploadModalOpen}
        modalHeading="Upload Content"
        primaryButtonText="Upload"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleFileUpload}
        onRequestClose={() => setIsUploadModalOpen(false)}
      >
        <Form>
          <FormGroup>
            <TextInput
              id="content-title"
              labelText="Title"
              placeholder="Enter content title"
              value={uploadForm.title}
              onChange={e => setUploadForm({ ...uploadForm, title: e.target.value })}
              required
            />
          </FormGroup>
          <FormGroup>
            <TextArea
              id="content-description"
              labelText="Description"
              placeholder="Enter content description"
              value={uploadForm.description}
              onChange={e => setUploadForm({ ...uploadForm, description: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Select
              id="content-subject"
              labelText="Subject"
              value={uploadForm.subject}
              onChange={e => setUploadForm({ ...uploadForm, subject: e.target.value })}
            >
              <SelectItem value="" text="Select a subject" />
              <SelectItem value="Mathematics" text="Mathematics" />
              <SelectItem value="Science" text="Science" />
              <SelectItem value="English" text="English" />
              <SelectItem value="History" text="History" />
              <SelectItem value="Geography" text="Geography" />
            </Select>
          </FormGroup>
          <FormGroup>
            <Select
              id="content-type"
              labelText="Content Type"
              value={uploadForm.contentType}
              onChange={e => setUploadForm({ ...uploadForm, contentType: e.target.value })}
            >
              <SelectItem value="course_material" text="Course Material" />
              <SelectItem value="assignment" text="Assignment" />
              <SelectItem value="quiz" text="Quiz" />
              <SelectItem value="lab_guide" text="Lab Guide" />
              <SelectItem value="study_guide" text="Study Guide" />
              <SelectItem value="worksheet" text="Worksheet" />
            </Select>
          </FormGroup>
          <FormGroup>
            <FileUploader
              accept={['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.png', '.jpg', '.mp4']}
              buttonLabel="Select file"
              buttonKind="primary"
              filenameStatus="edit"
              labelDescription="Max file size is 50MB. Supported formats: PDF, DOC, PPT, XLS, Images, Videos"
              labelTitle="Upload file"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  setUploadForm({ ...uploadForm, file });
                }
              }}
            />
          </FormGroup>
        </Form>
      </Modal>

      {/* Share Modal */}
      <Modal
        open={isShareModalOpen}
        modalHeading="Share Content"
        primaryButtonText="Share"
        secondaryButtonText="Cancel"
        onRequestClose={() => setIsShareModalOpen(false)}
      >
        <p>Select teachers to share this content with:</p>
        {/* Teacher selection would go here */}
      </Modal>
    </div>
  );
};

export default ContentLibrary;
